import React, {useState, useEffect, useRef, useCallback} from 'react'
import {
  AdminUploadForm,
  adminUploadFormComponents,
  AdminUploadManager,
  FileStatus,
} from '@modifi/plugin-documents'
import {Row, Col} from '@modifi/ui'
import {InferPropTypes} from '@modifi/typescript-utils'
import * as PropTypes from 'prop-types'
import ms from 'ms'

import {UploadDocumentsDialogContext} from './context'
import {FileUploadContainerWithSpacing, StyledH5, StyledParagraph} from './styles'
import DocumentTypeRow from './components/document-type-row-with-description'
import UploadCtaRow from './components/upload-cta-row'

const AdminFileQueue = (props: any) => (
  <adminUploadFormComponents.FileQueueEntry {...props} canChangeVisibility={false} />
)

const AdminFileList = (props: any) => (
  <adminUploadFormComponents.ExistingFilesList {...props} canChangeVisibility={false} />
)

const propTypes = {
  closeDialog: PropTypes.func.isRequired,
  dialogCleanup: PropTypes.func.isRequired,
  linkDocumentToPayment: PropTypes.func.isRequired,
  unlinkDocumentFromPayment: PropTypes.func.isRequired,
  inProgress: PropTypes.bool.isRequired,
  hasError: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  params: PropTypes.shape({
    transactionId: PropTypes.number.isRequired,
    availableDocumentTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    documents: PropTypes.arrayOf(
      PropTypes.shape({
        fileId: PropTypes.string.isRequired,
        documentType: PropTypes.string.isRequired,
        visibleForCustomer: PropTypes.bool.isRequired,
        canDeleteDocument: PropTypes.bool.isRequired,
        status: PropTypes.string.isRequired,
      }).isRequired
    ),
    onFileUploaded: PropTypes.func,
    onCancel: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
  }).isRequired,
}

const defaultProps = {}

type ManageDocumentProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const ManageDocuments: React.FC<ManageDocumentProps> = ({
  closeDialog,
  dialogCleanup,
  linkDocumentToPayment,
  unlinkDocumentFromPayment,
  inProgress,
  hasError,
  t,
  params,
}) => {
  const {
    transactionId,
    onSubmit,
    availableDocumentTypes,
    onFileUploaded: outerOnFileUploaded,
    onCancel,
  } = params
  const [linkingIsDone, setLinkingIsDone] = useState(true)
  const [unlinkingIsDone, setUnlinkingIsDone] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [uploadHasError, setUploadHasError] = useState(false)
  const [filesToLink, setFilesToLink] = useState(0)
  const [previouslyUploadedFiles, setPreviouslyUploadedFiles] = useState(
    params?.documents?.map(({fileId, documentType, canDeleteDocument, status}) => ({
      fileId,
      documentType,
      canDeleteDocument,
      status,
    })) || []
  )
  const errorMessageRef = useRef(null)

  useEffect(
    () => () => {
      dialogCleanup()
    },
    [dialogCleanup]
  )

  const checkIfSubmitFinished = useCallback(() => {
    // we want do use the onSubmit callback and closeDialog action as soon as the form got submitted and
    // all possible open actions are done && no errors occurred
    if (
      submitted &&
      linkingIsDone &&
      unlinkingIsDone &&
      hasError === false &&
      uploadHasError === false
    ) {
      onSubmit && onSubmit()
      closeDialog()
    }
  }, [hasError, closeDialog, onSubmit, linkingIsDone, unlinkingIsDone, submitted, uploadHasError])

  useEffect(() => {
    if (linkingIsDone && submitted && filesToLink === 0) {
      checkIfSubmitFinished()
    }
  }, [linkingIsDone, submitted, filesToLink]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileUploaded = useCallback(
    (fileData: any) => {
      setPreviouslyUploadedFiles((prevFiles: any) => [
        ...prevFiles,
        {fileId: fileData.fileId, documentType: fileData.documentType},
      ])
      Promise.resolve(linkDocumentToPayment(transactionId, fileData)).then(() => {
        setFilesToLink(prevFilesToLink => {
          const newFilesToLink = prevFilesToLink - 1
          const newLinkingIsDone = newFilesToLink <= 1
          setLinkingIsDone(newLinkingIsDone)
          return newFilesToLink
        })
      })
      outerOnFileUploaded && outerOnFileUploaded(fileData)
    },
    [linkDocumentToPayment, transactionId, outerOnFileUploaded]
  )

  const handleDeleteFile = useCallback((fileId: string) => {
    setUnlinkingIsDone(false)
    setPreviouslyUploadedFiles((prevFiles: any) =>
      prevFiles.map((file: any) => {
        if (file.fileId === fileId) {
          return {
            ...file,
            status: FileStatus.QUEUED_FOR_DELETION,
          }
        }
        return file
      })
    )
  }, [])

  const handleCancel = useCallback(() => {
    onCancel && onCancel()

    closeDialog()
  }, [closeDialog, onCancel])

  const getFileIdsToBeDeleted = useCallback(
    () =>
      previouslyUploadedFiles
        .filter(({status}) => status === FileStatus.QUEUED_FOR_DELETION)
        .map(({fileId}: {fileId: string}) => fileId),
    [previouslyUploadedFiles]
  )

  const handleSubmit = useCallback(async () => {
    const filesToBeDeleted = getFileIdsToBeDeleted()

    try {
      // perform deletion
      const hasErrors = await Promise.all(
        filesToBeDeleted.map((fileIdToUnlink: string) =>
          unlinkDocumentFromPayment(transactionId, fileIdToUnlink).then(
            () => {
              setPreviouslyUploadedFiles((prevFiles: any) =>
                prevFiles.filter(({fileId}: {fileId: string}) => !filesToBeDeleted.includes(fileId))
              )
              return true
            },
            (err: any) => {
              // error occurred while unlinking
              setPreviouslyUploadedFiles((prevFiles: any) =>
                prevFiles.map((file: any) => {
                  if (file.fileId === fileIdToUnlink) {
                    return {
                      ...file,
                      status: FileStatus.ERROR,
                      statusMessage: err.toString(),
                    }
                  }
                  return file
                })
              )
              return false
            }
          )
        )
      ).then(results => results.some(res => res === false))

      if (hasErrors === false) {
        setUnlinkingIsDone(true)
      }
      setSubmitted(true)
    } catch (e) {
      setUnlinkingIsDone(true)
      setSubmitted(false)
      setPreviouslyUploadedFiles((prevFiles: any) =>
        prevFiles.map((f: any) => ({
          ...f,
          status: f.status !== FileStatus.QUEUED_FOR_DELETION ? f.status : undefined,
        }))
      )
    }
  }, [getFileIdsToBeDeleted, unlinkDocumentFromPayment, transactionId])

  const handleUploadError = useCallback(() => {
    setUploadHasError(true)
  }, [])

  const handleUploadSubmit = useCallback(() => {
    setSubmitted(true)
  }, [])

  const handleAddFileToUploadQueue = useCallback(() => {
    setFilesToLink(prevFilesToLink => prevFilesToLink + 1)
    setLinkingIsDone(false)
  }, [])

  const handleRemoveFileFromUploadQueue = useCallback(() => {
    setFilesToLink(prevFilesToLink => prevFilesToLink - 1)
    setLinkingIsDone(filesToLink <= 1)
  }, [filesToLink])

  const filesToBeDeleted = getFileIdsToBeDeleted()

  return (
    <UploadDocumentsDialogContext.Provider value={{filesToBeDeleted}}>
      <Row>
        <Col col={10} offset={1}>
          <StyledH5 mb={20}>{t('dialog.upload-documents.headline')}</StyledH5>
        </Col>
      </Row>
      <StyledParagraph mb={30}>{t('dialog.upload-documents.file-type-hint')}</StyledParagraph>
      <span ref={errorMessageRef} />
      <AdminUploadManager
        onAddFileToUploadQueue={handleAddFileToUploadQueue}
        onRemoveFileFromUploadQueue={handleRemoveFileFromUploadQueue}
        onUploadError={handleUploadError}
        onFileUploadError={handleUploadError}
        onSubmit={handleUploadSubmit}
      >
        <AdminUploadForm
          previouslyUploadedFiles={previouslyUploadedFiles}
          availableDocumentTypes={availableDocumentTypes}
          multiple
          displayDocumentType
          withoutCta
          onFileUploaded={handleFileUploaded}
          onDeleteFile={handleDeleteFile}
          components={{
            documentTypeRow: DocumentTypeRow,
            fileUploadContainer: FileUploadContainerWithSpacing,
            fileQueueEntry: AdminFileQueue,
            existingFilesList: AdminFileList,
          }}
        />
        <UploadCtaRow
          withCancel
          expectedSubmitDuration={(1 + filesToLink + filesToBeDeleted.length) * ms('4s')}
          inProgress={inProgress}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
        />
      </AdminUploadManager>
    </UploadDocumentsDialogContext.Provider>
  )
}

ManageDocuments.propTypes = propTypes
ManageDocuments.defaultProps = defaultProps

export default ManageDocuments
